import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as AdminService from "../../services/Admin";

// ==================== Dashboard & Settings ====================

// Async thunks
export const fetchAdminDashboard = createAsyncThunk(
  "admin/fetchDashboard",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminDashboard();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch dashboard" },
      );
    }
  },
);

export const fetchSystemSettings = createAsyncThunk(
  "admin/fetchSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminService.getSystemSettings();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch settings" },
      );
    }
  },
);

export const saveSystemSettings = createAsyncThunk(
  "admin/saveSettings",
  async (settings, { rejectWithValue }) => {
    try {
      const response = await AdminService.updateSystemSettings(settings);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to save settings" },
      );
    }
  },
);

// ==================== Activities ====================

export const fetchAdminActivities = createAsyncThunk(
  "admin/fetchActivities",
  async (params, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminActivities(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch activities" },
      );
    }
  },
);

// ==================== Sites ====================

export const fetchAdminSites = createAsyncThunk(
  "admin/fetchSites",
  async (params, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminSites(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch sites" },
      );
    }
  },
);

export const fetchAdminSiteDetails = createAsyncThunk(
  "admin/fetchSiteDetails",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminSiteDetails(siteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch site details" },
      );
    }
  },
);

export const adminUpdateSiteStatus = createAsyncThunk(
  "admin/updateSiteStatus",
  async ({ siteId, status }, { rejectWithValue }) => {
    try {
      const response = await AdminService.updateSiteStatus(siteId, status);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to update site status" },
      );
    }
  },
);

export const adminStartSite = createAsyncThunk(
  "admin/startSite",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await AdminService.adminStartSite(siteId);
      return { siteId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to start site" },
      );
    }
  },
);

export const adminStopSite = createAsyncThunk(
  "admin/stopSite",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await AdminService.adminStopSite(siteId);
      return { siteId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to stop site" },
      );
    }
  },
);

export const adminRestartSite = createAsyncThunk(
  "admin/restartSite",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await AdminService.adminRestartSite(siteId);
      return { siteId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to restart site" },
      );
    }
  },
);

export const adminSoftDeleteSite = createAsyncThunk(
  "admin/softDeleteSite",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await AdminService.adminSoftDeleteSite(siteId);
      return { siteId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to delete site" },
      );
    }
  },
);

export const adminPermanentDeleteSite = createAsyncThunk(
  "admin/permanentDeleteSite",
  async (siteId, { rejectWithValue }) => {
    try {
      await AdminService.adminPermanentDeleteSite(siteId);
      return siteId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to permanently delete site",
        },
      );
    }
  },
);

export const adminRestoreSite = createAsyncThunk(
  "admin/restoreSite",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await AdminService.adminRestoreSite(siteId);
      return { siteId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to restore site" },
      );
    }
  },
);

export const adminTransferSite = createAsyncThunk(
  "admin/transferSite",
  async ({ siteId, newOwnerId }, { rejectWithValue }) => {
    try {
      const response = await AdminService.adminTransferSite(siteId, newOwnerId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to transfer site" },
      );
    }
  },
);

// ==================== Plans ====================

export const fetchAdminPlans = createAsyncThunk(
  "admin/fetchPlans",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AdminService.getAdminPlans();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch plans" },
      );
    }
  },
);

export const createAdminPlan = createAsyncThunk(
  "admin/createPlan",
  async (planData, { rejectWithValue }) => {
    try {
      const response = await AdminService.createPlan(planData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to create plan" },
      );
    }
  },
);

export const updateAdminPlan = createAsyncThunk(
  "admin/updatePlan",
  async ({ planId, planData }, { rejectWithValue }) => {
    try {
      const response = await AdminService.updatePlan(planId, planData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to update plan" },
      );
    }
  },
);

export const deleteAdminPlan = createAsyncThunk(
  "admin/deletePlan",
  async (planId, { rejectWithValue }) => {
    try {
      await AdminService.deletePlan(planId);
      return planId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to delete plan" },
      );
    }
  },
);

// ==================== User Management ====================

export const adminPermanentDeleteUser = createAsyncThunk(
  "admin/permanentDeleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      await AdminService.adminPermanentDeleteUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || {
          message: "Failed to permanently delete user",
        },
      );
    }
  },
);

const adminSlice = createSlice({
  name: "admin",
  initialState: {
    dashboard: null,
    settings: {},
    activities: [],
    activitiesPagination: null,
    sites: [],
    sitesPagination: null,
    selectedSite: null,
    plans: [],
    loading: false,
    settingsLoading: false,
    actionLoading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearAdminError: (state) => {
      state.error = null;
    },
    clearAdminSuccess: (state) => {
      state.successMessage = null;
    },
    clearSelectedSite: (state) => {
      state.selectedSite = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchAdminDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchAdminDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch dashboard";
      })
      // Settings
      .addCase(fetchSystemSettings.pending, (state) => {
        state.settingsLoading = true;
      })
      .addCase(fetchSystemSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload?.settings || {};
      })
      .addCase(fetchSystemSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.error = action.payload?.message || "Failed to fetch settings";
      })
      .addCase(saveSystemSettings.fulfilled, (state) => {
        state.successMessage = "Settings saved successfully";
      })
      .addCase(saveSystemSettings.rejected, (state, action) => {
        state.error = action.payload?.message || "Failed to save settings";
      })
      // Activities
      .addCase(fetchAdminActivities.fulfilled, (state, action) => {
        state.activities = action.payload?.activities || [];
        state.activitiesPagination = action.payload?.pagination || null;
      })
      // Sites list
      .addCase(fetchAdminSites.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAdminSites.fulfilled, (state, action) => {
        state.loading = false;
        state.sites = action.payload?.sites || [];
        state.sitesPagination = action.payload?.pagination || null;
      })
      .addCase(fetchAdminSites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch sites";
      })
      // Site details
      .addCase(fetchAdminSiteDetails.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(fetchAdminSiteDetails.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.selectedSite = action.payload?.site || null;
      })
      .addCase(fetchAdminSiteDetails.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to fetch site details";
      })
      // Site status update
      .addCase(adminUpdateSiteStatus.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(adminUpdateSiteStatus.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updatedSite = action.payload?.site;
        if (updatedSite) {
          const idx = state.sites.findIndex((s) => s.id === updatedSite.id);
          if (idx !== -1)
            state.sites[idx] = { ...state.sites[idx], ...updatedSite };
          if (state.selectedSite?.id === updatedSite.id)
            state.selectedSite = { ...state.selectedSite, ...updatedSite };
        }
        state.successMessage = "Site status updated";
      })
      .addCase(adminUpdateSiteStatus.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to update site status";
      })
      // Start/Stop/Restart site
      .addCase(adminStartSite.fulfilled, (state, action) => {
        const siteId = action.payload?.siteId;
        if (siteId) {
          const idx = state.sites.findIndex((s) => s.id === siteId);
          if (idx !== -1)
            state.sites[idx] = { ...state.sites[idx], status: "ACTIVE" };
        }
        state.successMessage = "Site started";
      })
      .addCase(adminStartSite.rejected, (state, action) => {
        state.error = action.payload?.message || "Failed to start site";
      })
      .addCase(adminStopSite.fulfilled, (state, action) => {
        const siteId = action.payload?.siteId;
        if (siteId) {
          const idx = state.sites.findIndex((s) => s.id === siteId);
          if (idx !== -1)
            state.sites[idx] = { ...state.sites[idx], status: "SUSPENDED" };
        }
        state.successMessage = "Site stopped";
      })
      .addCase(adminStopSite.rejected, (state, action) => {
        state.error = action.payload?.message || "Failed to stop site";
      })
      .addCase(adminRestartSite.fulfilled, (state, action) => {
        const siteId = action.payload?.siteId;
        if (siteId) {
          const idx = state.sites.findIndex((s) => s.id === siteId);
          if (idx !== -1)
            state.sites[idx] = { ...state.sites[idx], status: "ACTIVE" };
        }
        state.successMessage = "Site restarted";
      })
      .addCase(adminRestartSite.rejected, (state, action) => {
        state.error = action.payload?.message || "Failed to restart site";
      })
      // Soft delete
      .addCase(adminSoftDeleteSite.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(adminSoftDeleteSite.fulfilled, (state, action) => {
        state.actionLoading = false;
        const siteId = action.payload?.siteId;
        if (siteId) {
          const idx = state.sites.findIndex((s) => s.id === siteId);
          if (idx !== -1)
            state.sites[idx] = { ...state.sites[idx], status: "DELETED" };
        }
        state.successMessage = "Site deleted (30-day recovery available)";
      })
      .addCase(adminSoftDeleteSite.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to delete site";
      })
      // Permanent delete
      .addCase(adminPermanentDeleteSite.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(adminPermanentDeleteSite.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.sites = state.sites.filter((s) => s.id !== action.payload);
        state.successMessage = "Site permanently deleted";
      })
      .addCase(adminPermanentDeleteSite.rejected, (state, action) => {
        state.actionLoading = false;
        state.error =
          action.payload?.message || "Failed to permanently delete site";
      })
      // Restore site
      .addCase(adminRestoreSite.fulfilled, (state, action) => {
        const siteId = action.payload?.siteId;
        if (siteId) {
          const idx = state.sites.findIndex((s) => s.id === siteId);
          if (idx !== -1)
            state.sites[idx] = { ...state.sites[idx], status: "ACTIVE" };
        }
        state.successMessage = "Site restored";
      })
      .addCase(adminRestoreSite.rejected, (state, action) => {
        state.error = action.payload?.message || "Failed to restore site";
      })
      // Transfer site
      .addCase(adminTransferSite.fulfilled, (state, action) => {
        const updatedSite = action.payload?.site;
        if (updatedSite) {
          const idx = state.sites.findIndex((s) => s.id === updatedSite.id);
          if (idx !== -1)
            state.sites[idx] = { ...state.sites[idx], ...updatedSite };
        }
        state.successMessage = "Site ownership transferred";
      })
      .addCase(adminTransferSite.rejected, (state, action) => {
        state.error = action.payload?.message || "Failed to transfer site";
      })
      // Plans
      .addCase(fetchAdminPlans.fulfilled, (state, action) => {
        state.plans =
          action.payload?.data?.plans || action.payload?.plans || [];
      })
      .addCase(createAdminPlan.fulfilled, (state, action) => {
        state.plans.push(action.payload?.plan);
        state.successMessage = "Plan created successfully";
      })
      .addCase(updateAdminPlan.fulfilled, (state, action) => {
        const updated = action.payload?.plan;
        if (updated) {
          const idx = state.plans.findIndex((p) => p.id === updated.id);
          if (idx !== -1) state.plans[idx] = updated;
        }
        state.successMessage = "Plan updated successfully";
      })
      .addCase(deleteAdminPlan.fulfilled, (state, action) => {
        state.plans = state.plans.filter((p) => p.id !== action.payload);
        state.successMessage = "Plan deleted successfully";
      })
      // Permanent delete user
      .addCase(adminPermanentDeleteUser.fulfilled, (state) => {
        state.successMessage = "User permanently deleted";
      })
      .addCase(adminPermanentDeleteUser.rejected, (state, action) => {
        state.error =
          action.payload?.message || "Failed to permanently delete user";
      });
  },
});

export const { clearAdminError, clearAdminSuccess, clearSelectedSite } =
  adminSlice.actions;
export default adminSlice.reducer;
